import type { SectionType } from '@tunasong/models'
import { ChordLib, ScaleLib } from '@tunasong/music-lib'
import type { Chord, ChordType } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { generatePromptSpec } from '../../generate-prompt.js'
import type { AIConfig } from '../../schema/ai-schema.js'

/**
 * Melody suggestion prompt. F.2.1: scale-constrained, single candidate,
 * no section adaptation. See
 * `docs/superpowers/specs/2026-05-14-melody-suggestions-v1.md`.
 *
 * Chord-tone enumeration is computed deterministically server-side
 * (via `ChordLib.getNotes`) so the LLM doesn't need to do music theory.
 */

export interface MelodyChordRef {
  root: NoteName
  variant: ChordType
  /** Beats from start of section. */
  position: number
  /** Duration in beats. */
  duration: number
}

export interface MelodySuggestionPromptInput {
  scale: Scale
  chords: MelodyChordRef[]
  bpm?: number
  beatsPerMeasure?: number
  /** F.2.1 ignores this; F.2.3 uses for section-specific contour. */
  sectionType?: SectionType
  style?: string
  candidateCount?: number
  userConfig?: AIConfig
}

const formatChordTones = (chord: MelodyChordRef): string => {
  const chordObj: Chord = { root: chord.root, variant: chord.variant }
  // ChordLib.getNotes sorts the notes alphabetically. Re-order to put the root first
  // so the model sees tones in their conventional ordering (root, third, fifth, ...).
  const tones = Array.from(new Set(ChordLib.getNotes(chordObj)))
  const rootIndex = tones.indexOf(chord.root)
  const rootFirst = rootIndex >= 0 ? [...tones.slice(rootIndex), ...tones.slice(0, rootIndex)] : tones
  return rootFirst.join(', ')
}

const formatChordLine = (chord: MelodyChordRef): string => {
  const tones = formatChordTones(chord)
  const chordName = `${chord.root}${chord.variant}`
  return `    beat ${chord.position} (${chord.duration} beats): ${chordName} — chord tones: ${tones}`
}

export default ({
  scale,
  chords,
  bpm = 120,
  beatsPerMeasure = 4,
  sectionType,
  style,
  candidateCount = 1,
  userConfig,
}: MelodySuggestionPromptInput) => {
  // ScaleLib.getNotes returns intervals including the upper octave repetition (e.g., C major → [C,D,E,F,G,A,B,C]).
  // Dedupe to the unique pitches the model should pick from.
  const scaleNotes = Array.from(new Set(ScaleLib.getNotes(scale)))
  const scaleName = ScaleLib.getName(scale)
  const sectionLengthBeats = chords.reduce((acc, c) => acc + c.duration, 0)
  const chordLines = chords.map(formatChordLine).join('\n')

  const promptText = `Generate ${candidateCount === 1 ? 'one melody candidate' : `${candidateCount} melody candidates`} for the following section.

CONTEXT:
- Scale: ${scaleName}
- Allowed pitches (scale notes): [${scaleNotes.join(', ')}]
- Octave range: 3 to 5 inclusive
- Meter: ${beatsPerMeasure}/4 at ${bpm} bpm
- Section length: ${sectionLengthBeats} beats
${sectionType ? `- Section type: ${sectionType}` : ''}
${style ? `- Style hint: ${style}` : ''}

Chord progression:
${chordLines}

HARD CONSTRAINTS (must satisfy):
- Every note's pitch MUST appear in the allowed-pitches list above.
- Every note's octave MUST be between 3 and 5 inclusive.
- Notes must cover the section: positions range from 0 to under ${sectionLengthBeats}.

SOFT CONSTRAINTS (prefer):
- Notes on strong beats (1 and 3 in 4/4) should be chord tones of the chord active at that position.
- Prefer stepwise motion. Resolve leaps over a 6th by step in the opposite direction.
- Aim for an arch contour (rise then fall) unless the section asks otherwise.

Return JSON matching the requested schema. Include a one-line "rationale" describing why this melody fits the chords.`

  return generatePromptSpec({
    promptText,
    context: 'song:melody',
    userConfig: userConfig?.contexts['song:melody'],
  })
}
